import { queryOptions } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';

import {
  BulkRevisionResponse,
  BulkRevisionUpdateInput,
  ChuteScheduleCombinedResponse,
  ChuteSchedulerPayloadResponse,
  DonorScheduleSemenNotesDto,
  TopRevisionResponse,
  UpdateAspirationStatusPayload,
  UpdateAspirationTimePayload,
  UpdateRevisionPayload,
  UpdateRevisionsBatchPayload,
} from './types';

function getApiBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getAuthHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchDonorSchedulesByIds(
  token: AccessToken,
  ids: string[],
): Promise<DonorScheduleSemenNotesDto[]> {
  const url = `${getApiBaseUrl()}/api/DonorSchedule/by-ids`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(ids),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export async function bulkRevisionUpdate(
  token: AccessToken,
  input: BulkRevisionUpdateInput,
): Promise<BulkRevisionResponse> {
  const url = `${getApiBaseUrl()}/api/DonorSchedule/bulk-revision`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

export const chuteSchedulerApi = {
  fetchDonorSchedulesByIds,
  bulkRevisionUpdate,
};

// === REST migration functions ===

export async function fetchChuteScheduleCombined(
  token: AccessToken,
  siteId: string,
  date: string,
): Promise<ChuteScheduleCombinedResponse> {
  const params = new URLSearchParams();
  params.set('siteId', siteId);
  params.set('date', date);

  const url = `${getApiBaseUrl()}/api/v1/chute-scheduler/combined?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export async function fetchAllTopRevisions(
  token: AccessToken,
  date: string,
): Promise<TopRevisionResponse[]> {
  const params = new URLSearchParams();
  params.set('date', date);

  const url = `${getApiBaseUrl()}/api/v1/chute-scheduler/top-revisions?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export async function updateAspirationTimes(
  token: AccessToken,
  updates: UpdateAspirationTimePayload[],
): Promise<ChuteSchedulerPayloadResponse> {
  const url = `${getApiBaseUrl()}/api/v1/chute-scheduler/aspiration-times`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

export async function replaceRevision(
  token: AccessToken,
  donorScheduleId: string,
  revisionComment: string,
): Promise<ChuteSchedulerPayloadResponse> {
  const url = `${getApiBaseUrl()}/api/v1/chute-scheduler/revisions/${donorScheduleId}`;

  const response = await fetch(url, {
    method: 'PATCH',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(revisionComment),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

export async function updateRevisions(
  token: AccessToken,
  updates: UpdateRevisionPayload[],
): Promise<ChuteSchedulerPayloadResponse> {
  const url = `${getApiBaseUrl()}/api/v1/chute-scheduler/revisions`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

export async function updateRevisionsBatch(
  token: AccessToken,
  payload: UpdateRevisionsBatchPayload,
): Promise<ChuteSchedulerPayloadResponse> {
  const url = `${getApiBaseUrl()}/api/v1/chute-scheduler/revisions/batch`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

export async function updateAspirationStatusBatch(
  token: AccessToken,
  updates: UpdateAspirationStatusPayload[],
): Promise<ChuteSchedulerPayloadResponse> {
  const url = `${getApiBaseUrl()}/api/v1/chute-scheduler/aspiration-status/batch`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getAuthHeaders(token),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }

  return await response.json();
}

// === Query options for React Query ===

export const chuteScheduleCombinedOptions = (
  token: AccessToken,
  {
    siteId,
    date,
  }: {
    siteId: string;
    date: string;
  },
) =>
  queryOptions({
    queryKey: ['chute-scheduler', 'combined', { siteId, date }],
    enabled: !!token,
    async queryFn() {
      return fetchChuteScheduleCombined(token, siteId, date);
    },
  });

export async function fetchChuteScheduleCombinedV2(
  token: AccessToken,
  siteId: string,
  date: string,
): Promise<ChuteScheduleCombinedResponse> {
  const params = new URLSearchParams();
  params.set('siteId', siteId);
  params.set('date', date);

  const url = `${getApiBaseUrl()}/api/v2/chute-scheduler/combined?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

export const chuteScheduleCombinedV2Options = (
  token: AccessToken,
  {
    siteId,
    date,
  }: {
    siteId: string;
    date: string;
  },
) =>
  queryOptions({
    queryKey: ['chute-scheduler', 'combined-v2', { siteId, date }],
    enabled: !!token,
    async queryFn() {
      return fetchChuteScheduleCombinedV2(token, siteId, date);
    },
  });

export const chuteScheduleCombinedV3Options = (
  token: AccessToken,
  {
    siteId,
    date,
  }: {
    siteId: string;
    date: string;
  },
) =>
  queryOptions({
    queryKey: ['chute-scheduler', 'combined-v3', { siteId, date }],
    enabled: !!token,
    async queryFn(): Promise<ChuteScheduleCombinedResponse> {
      const params = new URLSearchParams();
      params.set('siteId', siteId);
      params.set('date', date);

      const url = `${getApiBaseUrl()}/api/v3/chute-scheduler/combined?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    },
  });

export const chuteScheduleCombinedMultiSiteOptions = (
  token: AccessToken,
  {
    siteIds,
    date,
  }: {
    siteIds: string[];
    date: string;
  },
) =>
  queryOptions({
    queryKey: ['chute-scheduler', 'combined-multi-site', { siteIds, date }],
    enabled: !!token && !!date && siteIds.length > 0,
    async queryFn(): Promise<ChuteScheduleCombinedResponse> {
      const params = new URLSearchParams();
      siteIds.forEach((siteId) => params.append('siteIds', siteId));
      params.set('date', date);

      const url = `${getApiBaseUrl()}/api/v4/chute-scheduler/combined?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    },
  });

export const allTopRevisionsOptions = (
  token: AccessToken,
  { date }: { date: string },
) =>
  queryOptions({
    queryKey: ['chute-scheduler', 'top-revisions', { date }],
    enabled: !!token,
    async queryFn() {
      return fetchAllTopRevisions(token, date);
    },
  });
